<template>
  <v-select
    ref="vSelect"
    v-model="selected"
    :options="computedOptions"
    :filterable="filterable"
    :placeholder="placeholder"
    :selectable="checkSelectable"
    :disabled="disabled"
    :reduce="reduce"
    :loading="computedLoading"
    :label="optionLabel"
    :clearable="clearable"
    :append-to-body="appendToBody"
    :calculate-position="handleCalculatePosition"
    class="ui-select"
    @open="onOpen"
    @close="onClose"
    @search="onSearchInput"
  >
    <template #option="option">
      <slot
        name="optionValue"
        :value="option"
      >
        {{ option[optionLabel] || option }}
      </slot>
    </template>
    <template #no-options="{ search, searching }">
      <slot
        name="no-options"
        :search="search"
        :searching="searching"
        :loading="computedLoading || isSearching"
      >
        <div
          v-if="computedLoading || isSearching"
          class="text-center flex justify-center items-center gap-[8px] h-[40px] text-[13px] text-[#737373]"
        >
          <b-spinner
            small
            variant="primary"
          />
          <span>Memuat data...</span>
        </div>
        <template v-else-if="searching">
          <em>'{{ search }}'</em> tidak ditemukan
        </template>
        <em v-else>Tidak ada data</em>
      </slot>
    </template>
    <template #list-footer>
      <li
        v-show="computedHasNextPage"
        ref="load"
        class="text-center flex justify-center items-center gap-[8px] h-[40px] text-[13px] text-[#737373]"
      >
        <b-spinner
          small
          variant="primary"
        />
        <span>Memuat data...</span>
      </li>
    </template>
  </v-select>
</template>

<script>
import vSelect from 'vue-select'
import { BSpinner } from 'bootstrap-vue'
import _ from 'lodash'

export default {
  name: 'Select',
  components: {
    vSelect,
    BSpinner,
  },
  props: {
    value: {
      type: [Object, String, Number, null],
      default: null,
    },
    defaultValue: {
      type: [Object, String, Number, null],
      default: null,
    },
    fetcher: {
      type: Object,
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    optionLabel: {
      type: String,
      default: 'name',
    },
    placeholder: {
      type: String,
      default: 'Pilih opsi',
    },
    hasNextPage: {
      type: Boolean,
      default: false,
    },
    debounceTimeout: {
      type: Number,
      default: 300,
    },
    reduce: {
      type: Function,
      default: option => option,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    calculatePosition: {
      type: Function,
      default: null,
    },
    selectable: {
      type: Function,
      default: null,
    },
  },
  data: () => ({
    observer: null,
    debouncedSearchFn: null,
    lastScrollPosition: 0,
    isOnSearch: false,
    isSearching: false,
  }),
  computed: {
    selected: {
      get() {
        return this.normalizeSelection(this.value ?? _.cloneDeep(this.defaultValue) ?? null)
      },
      set(newValue) {
        this.$emit('input', this.normalizeSelection(newValue))
      },
    },
    computedOptions() {
      if (this.fetcher && Array.isArray(this.fetcher.data)) {
        return this.fetcher.data
      }
      return this.options
    },
    computedHasNextPage() {
      if (this.fetcher && typeof this.fetcher.hasMore === 'boolean') {
        return this.fetcher.hasMore
      }
      return this.hasNextPage
    },
    computedLoading() {
      if (this.fetcher && typeof this.fetcher.isLoading === 'boolean') {
        return this.fetcher.isLoading
      }
      return this.loading
    },
  },
  watch: {
    computedOptions: {
      handler() {
        if (!this.observer || !this.$refs.load) return
        this.$nextTick(() => {
          this.restoreScrollPosition()
          if (this.observer && this.$refs.load) {
            this.observer.observe(this.$refs.load)
          }
        })
      },
      deep: false,
    },
  },
  created() {
    this.debouncedSearchFn = _.debounce(query => {
      this.isOnSearch = true
      if (this.fetcher && typeof this.fetcher.fetchData === 'function') {
        this.fetcher.fetchData({
          reset: true,
          options: {
            ...this.fetcher.options,
            search: query,
          },
        })
      }
      this.$emit('on-search', query)
      this.$nextTick(() => {
        this.isSearching = false
      })
    }, this.debounceTimeout)
  },
  mounted() {
    this.observer = new IntersectionObserver(this.infiniteScroll)
  },
  updated() {
    this.restoreScrollPosition()
  },
  methods: {
    checkSelectable(item) {
      if (this.disabled) return false
      if (typeof this.selectable === 'function') {
        return this.selectable(item)
      }
      return item ? !item.disabled : true
    },
    getDropdownMenu() {
      return (this.$refs.vSelect && this.$refs.vSelect.$refs && this.$refs.vSelect.$refs.dropdownMenu)
        || (this.$el ? this.$el.querySelector('.vs__dropdown-menu') : null)
    },
    restoreScrollPosition() {
      if (!this.isOnSearch && this.lastScrollPosition > 0) {
        const dropdown = this.getDropdownMenu()
        if (dropdown) {
          dropdown.scrollTop = this.lastScrollPosition
          this.lastScrollPosition = 0
        }
      }
    },
    normalizeSelection(val) {
      if (_.isObject(val)) {
        return ((_.get(val, this.optionLabel) ?? '') !== '') ? val : null
      }
      if (_.isString(val)) {
        return val.trim() !== '' ? val : null
      }
      return val ?? null
    },
    handleCalculatePosition(dropdownList, component, options) {
      if (dropdownList) {
        dropdownList.classList.add('ui-select-dropdown')
      }
      if (typeof this.calculatePosition === 'function') {
        return this.calculatePosition(dropdownList, component, options)
      }
      const { width, top, left } = options || {}
      if (dropdownList && width && top && left) {
        const dropdownEl = dropdownList
        dropdownEl.style.top = top
        dropdownEl.style.left = left
        dropdownEl.style.width = width
      }
      return undefined
    },
    onSearchInput(query) {
      this.lastScrollPosition = 0
      if (query && query.trim().length > 0) {
        this.isSearching = true
      } else {
        this.isSearching = false
      }
      if (this.debouncedSearchFn) {
        this.debouncedSearchFn(query)
      }
    },
    async onOpen() {
      await this.$nextTick()
      const dropdown = this.getDropdownMenu()
      if (dropdown) {
        dropdown.classList.add('ui-select-dropdown')
      }
      if (this.observer && this.$refs.load) {
        this.observer.observe(this.$refs.load)
      }
      this.$emit('open')
    },
    onClose() {
      this.lastScrollPosition = 0
      this.isOnSearch = false
      this.isSearching = false
      if (this.observer) {
        this.observer.disconnect()
      }
      this.$emit('on-close')
      this.$emit('close')
    },
    async infiniteScroll([{ isIntersecting, target }]) {
      if (isIntersecting && this.computedHasNextPage) {
        this.observer.unobserve(target)
        if (this.isOnSearch) this.isOnSearch = false
        const ul = target.offsetParent || this.getDropdownMenu()
        if (!ul) {
          this.observer.disconnect()
          return
        }
        this.lastScrollPosition = ul.scrollTop
        if (this.fetcher && typeof this.fetcher.fetchData === 'function') {
          this.fetcher.fetchData()
        }
        this.$emit('on-intersect')
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.v-select.ui-select,
.ui-select {
  font-size: 14px;

  .vs__dropdown-toggle {
    min-height: 36px !important;
    height: 36px !important;
    border-radius: 8px !important;
    border: 1px solid #E5E5E5 !important;
    padding: 0 10px !important;
    display: flex !important;
    align-items: center !important;
    box-shadow: none !important;
  }

  &.vs--open .vs__dropdown-toggle {
    border-radius: 8px !important;
    border-color: #E5E5E5 !important;
    box-shadow: none !important;
  }

  .vs__selected-options {
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
    width: 100% !important;
    flex: 1 1 auto !important;
    min-width: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    position: relative !important;
    overflow: hidden !important;
    flex-wrap: nowrap !important;
  }

  .vs__placeholder {
    color: #A3A3A3 !important;
    margin: 0 !important;
    margin-top: 0 !important;
    padding: 0 !important;
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    line-height: normal !important;
    opacity: 1 !important;
    pointer-events: none !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 100% !important;
  }

  &.vs--single .vs__selected,
  .vs__selected {
    color: #0A0A0A !important;
    margin: 0 !important;
    margin-top: 0 !important;
    padding: 0 !important;
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    line-height: normal !important;
    pointer-events: none !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 100% !important;
    display: block !important;
    transition: none !important;
  }

  .vs__actions {
    flex-shrink: 0 !important;
    display: flex !important;
    align-items: center !important;
    padding: 0 !important;

    .vs__open-indicator {
      margin-top: 0 !important;
    }
  }

  &.vs--single.vs--open {
    .vs__selected {
      opacity: 0.4 !important;
      margin: 0 !important;
      margin-top: 0 !important;
      transform: translateY(-50%) !important;
    }
    .vs__placeholder {
      margin: 0 !important;
      margin-top: 0 !important;
      transform: translateY(-50%) !important;
    }
  }

  .vs__search,
  .vs__search:focus {
    margin: 0 !important;
    padding: 0 !important;
    line-height: normal !important;
    border: none !important;
    box-shadow: none !important;
    height: 100% !important;
    width: 100% !important;
    min-width: 0 !important;
    background: transparent !important;
    position: relative !important;
    z-index: 1 !important;
  }

  &.vs--searching {
    .vs__placeholder,
    .vs__selected {
      display: none !important;
    }
  }

  &.vs--disabled {
    .vs__dropdown-toggle {
      background-color: #F5F5F5 !important;
      cursor: not-allowed !important;
    }
  }
}

// In-place dropdown positioning (when not append-to-body)
.v-select.ui-select:not(.vs--drop-up) > .vs__dropdown-menu {
  top: calc(100% + 4px);
}

.ui-select .vs__dropdown-menu,
.vs__dropdown-menu.ui-select-dropdown,
.ui-select-dropdown {
  z-index: 20 !important;
  border-radius: 8px !important;
  border: 1px solid #E5E5E5 !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  overflow-x: hidden !important;

  .vs__dropdown-option {
    white-space: normal !important;
    word-break: break-word !important;
    line-height: 1.4 !important;
  }
}
</style>
